import React, { createElement, useEffect, useState } from 'react';
import './index.scss';
import { Chart, Interval, Tooltip, Coordinate, Legend, View, registerShape, } from 'bizcharts';
import { Message } from '@alifd/next';
import { previewDataByField } from '../../../api/common/index';
import { dealArray, dealOrderByBoList, dealFieldByList } from '../../../utils';
import ErrorBoundary from '../../common/errorBoundary';
import ErrorComponent from '../../common/errorComponent';
const sliceNumber = 0.01;
registerShape('interval', 'sliceShape', {
    draw(cfg, container) {
        const points = cfg.points;
        let path = [];
        path.push(['M', points[0].x, points[0].y]);
        path.push(['L', points[1].x, points[1].y - sliceNumber]);
        path.push(['L', points[2].x, points[2].y - sliceNumber]);
        path.push(['L', points[3].x, points[3].y]);
        path.push('Z');
        path = this.parsePath(path);
        return container.addShape('path', {
            attrs: {
                fill: cfg.color,
                path: path,
                isCircle: true,
            },
        });
    },
});
const APieChart = (props, ref) => {
    const { dataSource, data, label, radius, tooltip, color, legend, isRing, backRing, backRingoffset, hasTitle, titleText, } = props;
    const [error, setError] = useState(false);
    const [errmsg, setErrmsg] = useState(null);
    const [total, setToal] = useState(1);
    const [idata, setIdata] = useState([]);
    const [value, setValue] = useState('value');
    const [series, setSeries] = useState('type');
    useEffect(() => {
        setError(false);
        try {
            if (dataSource?.value.length && dataSource?.group.length) {
                const limit = dataSource?.limit || [100];
                if (limit[0] > 1000) {
                    Message.error('最多显示前1000条!');
                    return;
                }
                const fl = dataSource?.filter || [];
                const params = {
                    groupId: dataSource.dataset.id,
                    type: 'PieChart',
                    fieldList: dealFieldByList([...dataSource.value, ...dataSource.group]),
                    horizontalAxis: dealArray(dataSource.group),
                    longitudinalAxis: dealArray(dataSource.value).map((e) => ({
                        ...e,
                        aggregateType: e?.aggregateType || 'COUNT',
                    })),
                    orderByBoList: dealOrderByBoList(dataSource.group),
                    resultCount: limit[0] === undefined ? 100 : limit[0],
                    customFilterVo: fl[0] === undefined
                        ? null
                        : {
                            logic: 'and',
                            filterVos: [dataSource?.filter[0] || null].filter((e) => e),
                        },
                };
                previewDataByField(params).then((res) => {
                    const fields = res?.data?.fields;
                    if (!fields) {
                        Message.error(res?.msg);
                        return;
                    }
                    const va = fields.find((e) => e.id === dataSource.value[0].id)?.fieldShortName || 'defalut';
                    const se = fields.find((e) => e.id === dataSource.group[0].id)?.fieldShortName || 'defalut';
                    setValue(va);
                    setSeries(se);
                    const tempData = res.data.data.map((e) => {
                        for (let item of dataSource.value) {
                            e[item.fieldShortName] = Number(e[item.fieldShortName]);
                        }
                        e.defalut = '默认';
                        return e;
                    });
                    setIdata(tempData);
                });
            }
            else {
                setValue('value');
                setSeries('type');
                setIdata(data);
            }
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('初始化数据出错,请在数据源面板重新配置字段或更新组件');
        }
    }, [dataSource]);
    useEffect(() => {
        try {
            getToltal();
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('统计总数出错,请检查数据来源是否合法或更新组件');
        }
    }, [idata]);
    label.style = {
        fontSize: label.size,
        fill: label.color,
    };
    const getContentFun = (item) => {
        try {
            switch (label.contenttag) {
                case 'tp':
                    return `${item[series]}\n${getPercent(item[value])}`;
                case 'tvp':
                    return `${item[series]}\n${item[value]}\n ${getPercent(item[value])}`;
                case 't':
                    return `${item[series]}`;
                case 'tv':
                    return `${item[series]}\n${item[value]}`;
                case 'v':
                    return `${item[value]}`;
                case 'p':
                    return `${getPercent(item[value])}`;
                case 'vp':
                    return `${item[value]}\n${getPercent(item[value])}`;
                default:
                    return `${item[series]}\n${getPercent(item[value])}`;
            }
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('标签文本匹配出错,请在属性面板检查配置项或更新组件');
        }
    };
    const IndicatorCard = ({ info, color, width }) => {
        return (createElement("div", { className: "indicator-card base_bg", style: { borderColor: color, width: width } },
            createElement("div", null,
                createElement("p", null, info?.type),
                createElement("p", { style: { fontSize: '16px' } }, info?.value)),
            createElement("div", { style: { marginLeft: '20px' } },
                createElement("p", { style: { color: 'gray' } }, "\u5360\u6BD4"),
                createElement("p", null, getPercent(info?.value)))));
    };
    const getToltal = () => {
        try {
            let num = idata.reduce((pre, next) => {
                return pre + next[value];
            }, 0);
            setToal(num);
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('统计总数出错,请检查数据来源是否合法或更新组件');
        }
    };
    const getRadio = (num, flag = true) => {
        try {
            if (legend?.display === 'horizontal') {
                return '100%';
            }
            if (flag) {
                return Math.min(num, 100) + '%';
            }
            else {
                return Math.max(100 - num, 0) + '%';
            }
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('计算比例出错,请检查数据来源是否合法或更新组件');
        }
    };
    const getLegendText = (item) => {
        try {
            switch (tooltip.content) {
                case 'p':
                    return { name: item?.type, value: getPercent(item.value) };
                case 'vp':
                    return { name: item?.type, value: getPercent(item.value) };
                case 't':
                    return { name: item?.type, value: item?.type };
                case 'v':
                    return { name: item?.type, value: item.value };
            }
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('图例文本匹配出错,请检查数据来源是否合法或更新组件');
        }
    };
    const getPercent = (item) => {
        try {
            return `${((item / total) * 100).toFixed(props.decimal)}%`;
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('计算百分比出错,请检查数据来源是否合法或更新组件');
        }
    };
    const colorArr = color ? color.split(',') : [];
    return (createElement(ErrorBoundary, null, error ? (createElement(ErrorComponent, { ref: ref, errmsg: errmsg })) : (createElement("div", { ref: ref },
        hasTitle ? (createElement("div", { className: "datasettitle" }, titleText || dataSource?.dataset?.name)) : null,
        createElement("div", { className: "pie-box", style: { flexDirection: legend?.display === 'vertical' ? 'row' : 'column-reverse' } },
            createElement(Chart, { height: 400, data: idata, autoFit: true, forceUpdate: true },
                backRing ? (createElement(View, { data: [{ percent: 9999999 }] },
                    createElement(Tooltip, { visible: false }),
                    createElement(Coordinate, { type: "theta", radius: radius / 100, innerRadius: isRing ? props.innerRadius || 0.75 : 0 }),
                    createElement(Interval, { position: "percent", adjust: "stack", color: "#F7F7F7", size: radius + (backRingoffset || 5), style: { fillOpacity: 0.6 } }))) : null,
                createElement(Coordinate, { type: "theta", radius: radius / 100, innerRadius: isRing ? props.innerRadius || 0.75 : 0 }),
                createElement(Tooltip, Object.assign({}, tooltip, { formatter: getLegendText })),
                createElement(Legend, { position: legend?.position, visible: legend?.visible && legend?.type === 'native' }),
                createElement(Interval, { position: value, adjust: "stack", color: [series, colorArr], label: label.visible
                        ? [
                            value,
                            {
                                ...label,
                                layout: { type: 'pie-spider', cfg: { action: 'ellipsis' } },
                                type: 'pie',
                                offset: label.labelOffset || 80,
                                content: getContentFun,
                            },
                        ]
                        : false, shape: "sliceShape" })),
            legend?.type === 'card' && legend.visible ? (createElement("div", { style: { width: legend?.type === 'native' ? '100%' : getRadio(legend?.ratio) } },
                createElement("div", { className: "legend-card", style: {
                        width: getRadio(legend?.ratio, false),
                        flexDirection: legend?.display === 'vertical' ? 'column' : 'row',
                    } }, props.data.map((e, i) => (createElement(IndicatorCard, { width: legend?.legendWidth || 100 + '%', info: e, color: colorArr[i] })))))) : null)))));
};
export default React.forwardRef(APieChart);
